import { z } from 'zod';
import { isoDateTime } from './primitives';
import { artifactSource, type ArtifactDraft } from './episode';

/**
 * Research report contract (Master Spec §13.1 "Output — report artifact", §6.5).
 *
 * WP-R4. The final session of a multi-session research run (WP-R2) produces a
 * structured **Markdown report** — title, executive summary, findings with inline
 * citations, and a sources list — and the platform persists it as an `artifact`
 * (`kind:'report'`) on the run. The UI (WP-R5) renders it and offers download as
 * Markdown / PDF.
 *
 * This module defines the STRUCTURED report the runtime writes and the UI reads,
 * plus a pure mapper to the generic `ArtifactDraft` (§6.5) the existing artifact
 * path persists. The report body is stored as `format:'markdown'` text (so the
 * generic artifact renderer and MD/PDF export work unchanged), and the report's
 * web sources become the artifact's `sources` column (hosted-research citations).
 *
 * A report cites two kinds of source (§13.1 "web URLs + referenced email
 * messageIds"): `web` sources (a retrieved URL) and `email` sources (a mailbox
 * message the read-only `email.search` target surfaced). Inline citations in the
 * Markdown reference sources by their 1-based index in `sources`.
 */

/**
 * One cited source underpinning the report (§13.1). A discriminated union on
 * `type` so a source is EITHER a web URL or an email messageId — the two research
 * targets (§13.1 `web` / `email`). Extensible: WP-R6 (HubSpot etc.) adds a variant
 * with no consumer refactor.
 */
export const reportSource = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('web'),
    /** The retrieved page URL. */
    url: z.string().url(),
    /** Human-readable source title (page/article title). */
    title: z.string().min(1),
    /** When the run accessed it (ISO-8601). */
    accessedAt: isoDateTime,
  }),
  z.object({
    type: z.literal('email'),
    /** The connected-mailbox message id the finding draws on (read-only, §13.1). */
    messageId: z.string().min(1),
    /** Human-readable label (e.g. subject line) for the run/report view. */
    title: z.string().min(1),
    /** When the run read it (ISO-8601). */
    accessedAt: isoDateTime,
  }),
]);
export type ReportSource = z.infer<typeof reportSource>;

/**
 * One finding in the report body (§13.1 "findings with inline citations"). The
 * `markdown` carries the finding's prose; `citations` are the 1-based indices into
 * the report's `sources` array that the prose cites inline (`[1]`, `[2]`, …). Kept
 * structured (not just concatenated Markdown) so WP-R5 can render/validate the
 * citation ↔ source linkage and export cleanly.
 */
export const reportFinding = z.object({
  heading: z.string().min(1),
  markdown: z.string().min(1),
  /** 1-based indices into `sources` cited by this finding. */
  citations: z.array(z.number().int().positive()).default([]),
});
export type ReportFinding = z.infer<typeof reportFinding>;

/**
 * The structured research report (§13.1). The runtime (WP-R2) writes it; the UI
 * (WP-R5) renders + exports it. `sources` is the ordered citation list inline
 * citations reference by 1-based index. A cross-field refinement fails closed if a
 * finding cites a source index that does not exist — a malformed report (dangling
 * citation) is rejected, not silently persisted.
 */
export const researchReport = z
  .object({
    title: z.string().min(1),
    /** Executive summary (Markdown, §13.1). */
    executiveSummary: z.string().min(1),
    /** Findings, each with inline citations (§13.1). At least one. */
    findings: z.array(reportFinding).min(1),
    /** Ordered citation list — web URLs + referenced email messageIds (§13.1). */
    sources: z.array(reportSource).default([]),
  })
  .superRefine((report, ctx) => {
    const maxIndex = report.sources.length;
    report.findings.forEach((finding, fi) => {
      finding.citations.forEach((cite, ci) => {
        if (cite > maxIndex) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['findings', fi, 'citations', ci],
            message: `Citation [${cite}] has no matching source (only ${maxIndex} source(s)).`,
          });
        }
      });
    });
  });
export type ResearchReport = z.infer<typeof researchReport>;

/**
 * The sentinel `title` a research finalisation carries its STRUCTURED report on
 * (§13.1, WP-R2/WP-R4). When the model calls the `research.submit_report` control
 * tool, the SDK adapter attaches the raw report as a `structured` artifact under
 * this fixed title; the episode runner extracts that carrier's `body.data`,
 * validates + persists it via `finaliseResearchReport`, and DROPS the carrier so the
 * report lands exactly once as the canonical `kind:'report'` artifact.
 *
 * It lives in `@app/shared` (not the app or the vendor package) so BOTH the
 * boundary-clean SDK adapter (I4) and the app-side runtime reference ONE constant —
 * the report rides across the AgentRuntime port as plain JSON, never a vendor type.
 */
export const RESEARCH_REPORT_ARTIFACT_TITLE = '__research_report__';

/**
 * Render the structured report to a single Markdown document (§13.1). Deterministic
 * — the same report always yields the same Markdown, so snapshots/exports are
 * stable. Inline citations already live in each finding's `markdown`; this adds the
 * title, executive-summary, and a trailing numbered Sources section keyed to the
 * same 1-based indices.
 */
export function renderReportMarkdown(report: ResearchReport): string {
  const lines: string[] = [];
  lines.push(`# ${report.title}`, '');
  lines.push('## Executive Summary', '', report.executiveSummary.trim(), '');
  lines.push('## Findings', '');
  for (const finding of report.findings) {
    lines.push(`### ${finding.heading}`, '', finding.markdown.trim(), '');
  }
  if (report.sources.length > 0) {
    lines.push('## Sources', '');
    report.sources.forEach((source, i) => {
      const n = i + 1;
      if (source.type === 'web') {
        lines.push(`${n}. [${source.title}](${source.url})`);
      } else {
        lines.push(`${n}. ${source.title} (email: ${source.messageId})`);
      }
    });
    lines.push('');
  }
  return lines.join('\n').trimEnd() + '\n';
}

/**
 * Map a validated report to the generic `ArtifactDraft` (§6.5) the existing
 * artifact path persists. The Markdown becomes the artifact `body`
 * (`format:'markdown'`, text), and the report's WEB sources become the artifact's
 * `sources` column (the hosted-research citation shape, url/title/accessedAt).
 * Email sources are NOT web citations, so they do NOT go in that column; they are
 * preserved inside the rendered report body for the UI, which renders the full
 * source list from the report Markdown itself.
 *
 * The finalisation seam (WP-R2 → `finaliseResearchReport`) calls this, then hands
 * the draft to `appendArtifact`. Pure + vendor-free so it lives in shared.
 */
export function reportToArtifactDraft(report: ResearchReport): ArtifactDraft {
  const webSources = report.sources
    .filter((s): s is Extract<ReportSource, { type: 'web' }> => s.type === 'web')
    .map((s) => ({ url: s.url, title: s.title, accessedAt: s.accessedAt }));
  // Validate the projected web citations against the artifact-source contract so a
  // malformed URL fails closed at the shared boundary (defence in depth).
  const sources = webSources.length > 0 ? z.array(artifactSource).parse(webSources) : null;
  return {
    kind: 'report',
    title: report.title,
    body: { format: 'markdown', text: renderReportMarkdown(report) },
    sources,
  };
}
